import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GlobalStatsBar from './GlobalStatsBar';
import NotificationBell from './NotificationBell';
import NotificationAlertModal from './NotificationAlertModal';
import axios from 'axios';
import { 
  LayoutDashboard, 
  FileText, 
  Briefcase, 
  MessageSquare, 
  Settings, 
  LogOut,
  Menu,
  X,
  Bell,
  Clock,
  Calendar,
  Edit3,
  Globe,
  BookOpen,
  Megaphone,
  BarChart2,
  Users,
  UserPlus,
  UserCheck,
  Activity,
  FileSignature,
  Target,
  Workflow,
  CheckSquare,
  GitPullRequest,
  Mail,
  Sparkles,
  HelpCircle,
  Building2,
  Plug,
  Database,
  Shield,
  Download,
  TrendingUp,
  DollarSign,
  Send,
  Handshake,
  IndianRupee
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLayout = () => {
  const { admin, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false); // Default to closed
  const [selectedLogo, setSelectedLogo] = useState('logo_monogram'); // Default
  const [agentStatus, setAgentStatus] = useState('available');
  const [pendingChatsCount, setPendingChatsCount] = useState(0);
  const [toolSettings, setToolSettings] = useState(null);
  const [unreadEmailCount, setUnreadEmailCount] = useState(0);

  useEffect(() => {
    fetchSelectedLogo();
    fetchToolSettings();
    updateAgentStatus('available'); // Set available on login
    startPendingChatsPolling();
    fetchUnreadEmailCount();
    
    // Poll for unread emails every 30 seconds
    const emailInterval = setInterval(fetchUnreadEmailCount, 30000);
    
    // Listen for email read status changes
    const handleEmailStatusChange = () => {
      fetchUnreadEmailCount();
    };
    window.addEventListener('emailReadStatusChanged', handleEmailStatusChange);
    
    return () => {
      updateAgentStatus('offline'); // Set offline on logout/unmount
      clearInterval(emailInterval);
      window.removeEventListener('emailReadStatusChanged', handleEmailStatusChange);
    };
  }, []);

  const fetchSelectedLogo = async () => {
    try {
      const response = await axios.get(`${API}/settings/selected_logo`);
      setSelectedLogo(response.data.setting_value);
    } catch (error) {
      console.log('Using default logo (monogram)');
    }
  };

  const fetchToolSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/smart-tools/settings`, {
        headers: getAuthHeader()
      });
      setToolSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch tool settings:', error);
      // Default to all enabled if fetch fails
      setToolSettings({
        lead_scoring_enabled: true,
        lead_assignment_enabled: true,
        lead_pull_enabled: true,
        knowledge_center_enabled: true,
        workflows_enabled: true,
        ai_followup_enabled: true,
      });
    }
  };


  const updateAgentStatus = async (status) => {
    try {
      await axios.post(`${API}/chatbot/admin/agent/status`, 
        { status, active_chats: 0 },
        { headers: getAuthHeader() }
      );
      setAgentStatus(status);
    } catch (error) {
      console.error('Failed to update agent status:', error);
    }
  };

  const toggleAgentStatus = () => {
    const newStatus = agentStatus === 'available' ? 'away' : 'available';
    updateAgentStatus(newStatus);
  };

  const fetchUnreadEmailCount = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/emails?folder=inbox&is_read=false`, {
        headers: getAuthHeader()
      });
      const unreadCount = response.data.length || 0;
      setUnreadEmailCount(unreadCount);
    } catch (error) {
      console.error('Failed to fetch unread email count:', error);
    }
  };

  const startPendingChatsPolling = () => {
    fetchPendingChatsCount();
    setInterval(() => {
      fetchPendingChatsCount();
    }, 5000); // Check every 5 seconds
  };

  const fetchPendingChatsCount = async () => {
    try {
      const response = await axios.get(`${API}/chatbot/admin/chat/pending`, {
        headers: getAuthHeader()
      });
      setPendingChatsCount(response.data.length);
    } catch (error) {
      console.error('Failed to fetch pending chats:', error);
    }
  };


  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Organized menu structure based on permission groups
  const menuGroups = [
    {
      label: 'Core',
      items: [
        { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', badge: null, permission: 'view_dashboard' },
        { path: '/admin/live-chats', icon: MessageSquare, label: 'Live Chats', badge: pendingChatsCount > 0 ? pendingChatsCount : null, permission: 'view_dashboard' },
      ]
    },
    {
      label: 'User & Role Management',
      items: [
        { path: '/admin/users', icon: Users, label: 'User Management', badge: null, roles: ['super_admin'] },
        { path: '/admin/roles', icon: Shield, label: 'Role Management', badge: null, roles: ['super_admin'] },
      ]
    },
    {
      label: 'Lead & Client Management',
      items: [
        { path: '/admin/leads', icon: UserPlus, label: 'Leads', badge: null, permission: 'manage_leads' },
        { path: '/admin/proposals', icon: FileText, label: 'Proposals', badge: null, roles: ['admin', 'super_admin', 'sales'] },
        { path: '/admin/agreements', icon: FileSignature, label: 'Agreements', badge: null, permission: 'view_agreements' },
        { path: '/admin/invoices', icon: FileText, label: 'Invoices', badge: null, roles: ['admin', 'super_admin'] },
        { path: '/admin/clients', icon: UserCheck, label: 'Clients', badge: null, permission: 'manage_clients' },
        { path: '/admin/scheduler', icon: Calendar, label: 'Scheduler', badge: null, permission: 'manage_activities' },
      ]
    },
    {
      label: 'Smart Lead Tools',
      items: [
        { path: '/admin/lead-scoring', icon: Target, label: 'Lead Scoring', badge: null, permission: 'manage_lead_scoring', enabledKey: 'lead_scoring_enabled' },
        { path: '/admin/lead-assignment', icon: Activity, label: 'Lead Assignment', badge: null, permission: 'manage_lead_assignment', enabledKey: 'lead_assignment_enabled' },
        { path: '/admin/lead-pull', icon: Download, label: 'Lead Pull', badge: null, permission: 'manage_lead_pull', enabledKey: 'lead_pull_enabled' },
        { path: '/admin/knowledge-center', icon: BookOpen, label: 'Knowledge Center', badge: null, permission: 'view_dashboard', enabledKey: 'knowledge_center_enabled' },
        { path: '/admin/workflows', icon: Workflow, label: 'Workflows', badge: null, permission: 'manage_workflows', enabledKey: 'workflows_enabled' },
        { path: '/admin/tasks', icon: CheckSquare, label: 'Tasks', badge: null, permission: 'view_dashboard' },
        { path: '/admin/ai-followup', icon: Sparkles, label: 'AI Follow-Up', badge: null, permission: 'use_ai_followup', enabledKey: 'ai_followup_enabled' },
        { path: '/admin/smart-tools-settings', icon: Settings, label: 'Tools Settings', badge: null, roles: ['super_admin'] },
      ]
    },
    {
      label: 'Content & Settings',
      items: [
        { path: '/admin/services', icon: Briefcase, label: 'Services', badge: null, permission: 'manage_services' },
        { path: '/admin/country-services', icon: Globe, label: 'Country Services', badge: null, permission: 'manage_country_services' },
        { path: '/admin/testimonials', icon: MessageSquare, label: 'Testimonials', badge: null, permission: 'manage_testimonials' },
        { path: '/admin/blogs', icon: BookOpen, label: 'Blogs', badge: null, permission: 'manage_blogs' },
        { path: '/admin/announcements', icon: Megaphone, label: 'Announcements', badge: null, permission: 'manage_announcements' },
        { path: '/admin/announcement-automation', icon: Megaphone, label: '🤖 Auto Announcements', badge: null, permission: 'manage_announcements' },
        { path: '/admin/content', icon: Edit3, label: 'Content Editor', badge: null, permission: 'manage_content' },
        { path: '/admin/newsletter', icon: Mail, label: 'Newsletter', badge: null, permission: 'manage_newsletter' },
        { path: '/admin/callback-master', icon: Clock, label: 'Call Back Master', badge: null, permission: 'manage_callbacks' },
        { path: '/admin/chatbot-settings', icon: MessageSquare, label: 'Chatbot Q&A', badge: null, permission: 'manage_chatbot' },
        { path: '/admin/settings', icon: Settings, label: 'Settings', badge: null, permission: 'manage_settings' },
        { path: '/admin/source-master', icon: Database, label: 'Source Master', badge: null, roles: ['admin', 'super_admin'] },
        { path: '/admin/company-settings', icon: Building2, label: 'Company Settings', badge: null, roles: ['admin', 'super_admin'] },
        { path: '/admin/spinner-settings', icon: Settings, label: 'Spinner Settings', badge: null, roles: ['super_admin'] },
        { path: '/admin/one92-settings', icon: Building2, label: 'One92 Settings', badge: null, permission: 'manage_one92_settings' },
      ]
    },
    {
      label: 'Pricing Intelligence',
      items: [
        { path: '/admin/competitor-pricing', icon: TrendingUp, label: 'Competitor Pricing', badge: null, roles: ['super_admin'] },
        { path: '/admin/pricing-rules', icon: DollarSign, label: 'Pricing Rules', badge: null, roles: ['super_admin', 'admin'] },
        { path: '/admin/margin-management', icon: TrendingUp, label: 'Margin Management', badge: null, roles: ['super_admin', 'admin'] },
      ]
    },
    {
      label: 'Business Partners',
      items: [
        { path: '/admin/partner-management', icon: Handshake, label: 'Partner Management', badge: null, roles: ['super_admin', 'admin'] },
        { path: '/admin/master-partner-management', icon: Building2, label: 'M Partner Management', badge: null, roles: ['super_admin', 'admin'] },
        { path: '/admin/master-partner-commissions', icon: IndianRupee, label: 'M Partner Commissions', badge: null, roles: ['super_admin', 'admin'] },
      ]
    },
    {
      label: 'Analytics & Integrations',
      items: [
        { path: '/admin/analytics', icon: BarChart2, label: 'Analytics', badge: null, permission: 'view_analytics' },
        { path: '/admin/integrations', icon: Plug, label: 'Integrations', badge: null, permission: 'manage_integrations' },
        { path: '/admin/emails', icon: Mail, label: 'Email', badge: null, permission: 'manage_leads' },
        { path: '/admin/communications', icon: Send, label: 'Communications', badge: null, permission: 'manage_leads' },
        { path: '/admin/data-management', icon: Database, label: 'Data Management', badge: null, permission: 'manage_data' },
      ]
    },
    {
      label: 'Support & Help',
      items: [
        { path: '/admin/help', icon: HelpCircle, label: 'Help Center', badge: null, permission: 'view_help_center' },
      ]
    }
  ];

  // Filter menu items based on role and permissions
  const hasAccess = (item) => {
    // Check if tool is enabled (for Smart Lead Tools) - applies to ALL users including super admin
    if (item.enabledKey && toolSettings && toolSettings[item.enabledKey] !== true) {
      // Exception: Super admin can always access Tools Settings even if tool is disabled
      if (item.path !== '/admin/smart-tools-settings') {
        return false;
      }
    }
    
    // Super admin has access to everything (that is enabled)
    if (admin?.role === 'super_admin') return true;
    
    // Check role-specific items
    if (item.roles && !item.roles.includes(admin?.role)) return false;
    
    // Check permission-based items
    if (item.permission && !admin?.permissions?.includes(item.permission)) return false;
    
    return true;
  };

  // Filter groups and their items
  const visibleMenuGroups = menuGroups.map(group => ({
    ...group,
    items: group.items.filter(hasAccess)
  })).filter(group => group.items.length > 0);

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const handleNotificationClick = () => {
    setNotificationOpen(!notificationOpen);
  };

  const handleViewAllConsultations = () => {
    setNotificationOpen(false);
    navigate('/admin/consultations');
  };

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2 className="heading-2">VISAASURE</h2>
          <button className="mobile-close" onClick={() => setSidebarOpen(false)}>
            <X size={24} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {visibleMenuGroups.map((group, groupIndex) => (
            <div key={groupIndex} className="menu-group">
              <div className="menu-group-label">{group.label}</div>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                  >
                    <div className="nav-item-content">
                      <Icon size={20} />
                      <span>{item.label}</span>
                    </div>
                    {item.badge > 0 && (
                      <span className="nav-badge">{item.badge}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="admin-info">
            <p className="body-small">Logged in as</p>
            <p className="body-medium">{admin?.username}</p>
            {admin?.role && (
              <p className="caption" style={{ marginTop: '4px', color: '#666' }}>
                {admin.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </p>
            )}
          </div>
          <button onClick={handleLogout} className="btn-secondary logout-btn">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Overlay for mobile when sidebar is open */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(false)}
      ></div>

      {/* Main Content */}
      <div className={`admin-main ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <header className="admin-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button 
              className="hamburger-menu-btn" 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              title={sidebarOpen ? 'Close Menu' : 'Open Menu'}
            >
              <Menu size={24} />
            </button>
            <div className="admin-header-brand">
              <img 
                src={`/${selectedLogo}.png`} 
                alt="Visaasure Logo" 
                className="admin-header-logo"
              />
              <h1 className="heading-2 admin-header-title">
                {admin?.role === 'super_admin' ? 'Admin Console' : 
                 admin?.role === 'sales' ? 'Sales Console' :
                 admin?.role === 'process_consultant' ? 'Process Console' : 
                 'Console'}
              </h1>
            </div>
            
            {/* Page Stats - Global Stats Bar */}
            <div id="header-stats-container" style={{ 
              display: 'flex', 
              gap: '0.5rem', 
              marginLeft: 'auto',
              marginRight: '1rem',
              alignItems: 'center'
            }}>
              <GlobalStatsBar />
            </div>
          </div>
          <div className="header-actions">
            <button
              onClick={toggleAgentStatus}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                background: agentStatus === 'available' ? '#22c55e' : '#f59e0b',
                color: 'white',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                marginRight: '12px'
              }}
            >
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'white'
              }}></span>
              {agentStatus === 'available' ? 'Available' : 'Away'}
            </button>
            
            {/* Email Notification Icon */}
            <button
              onClick={() => navigate('/admin/emails')}
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: 'none',
                background: unreadEmailCount > 0 ? '#f0f9ff' : 'transparent',
                cursor: 'pointer',
                marginRight: '8px',
                transition: 'background 0.2s'
              }}
              title={`${unreadEmailCount} unread emails`}
            >
              <Mail 
                size={20} 
                color={unreadEmailCount > 0 ? '#0ea5e9' : '#6b7280'}
                strokeWidth={2}
              />
              {unreadEmailCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  background: '#ef4444',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  borderRadius: '10px',
                  padding: '2px 6px',
                  minWidth: '18px',
                  textAlign: 'center',
                  lineHeight: '1'
                }}>
                  {unreadEmailCount > 99 ? '99+' : unreadEmailCount}
                </span>
              )}
            </button>
            
            <NotificationBell />
            <span className="body-small user-name">{admin?.username}</span>
          </div>
        </header>

        <main className="admin-content">
          <Outlet />
        </main>
        
        {/* Notification Alert Modal */}
        <NotificationAlertModal />
      </div>
    </div>
  );
};

export default AdminLayout;